import { Link as CLink } from "@chakra-ui/react";
import { Link, router } from "@inertiajs/react";
import { player_path, my_games_path, events_path, event_path } from "@/routes";
import { Game, Event } from "@/types";
import { useAuth } from "@/hooks/useAuth";
import { prefectures, ageGroups, genders, formatDate, formatTime } from "@/lib/utils";

interface Props {
  latest_rating_score: number;
  games_count: number;
  winning_rate: number | null;
  scoring_rate: number;
  latest_games: Game[];
  participated_events: Event[];
}

const handleRowClick = (event: Event) => {
  router.visit(event_path(event.id));
};

export default function Index({ latest_rating_score, games_count, winning_rate, scoring_rate, latest_games, participated_events }: Props) {
  const currentPlayer = useAuth().player!;
  const prefecture = prefectures[currentPlayer.prefecture as string];
  const ageGroup = ageGroups[currentPlayer.age_group as string];
  const gender = genders[currentPlayer.gender as string];

  return (
    <div>
      <div className="mx-auto px-4 sm:px-6 lg:px-8 bg-slate-800 text-white p-4 text-center">
        <h1 className="text-3xl font-bold tracking-tight">ダッシュボード</h1>
      </div>

      <section className="bg-white text-center py-6 shadow">
        <h2 className="text-3xl font-bold">{currentPlayer.display_name}</h2>
        <p className="mt-1 text-gray-600">ID: {currentPlayer.player_id}</p>
        <p className="mt-1 text-gray-600">
          {prefecture}・{gender}({ageGroup})
        </p>
      </section>

      <div className="max-w-4xl mx-auto">
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 m-6 text-center">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-2xl font-bold">{latest_rating_score}</p>
            <p className="text-sm/6 font-medium text-gray-400">レーティング</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-2xl font-bold">{games_count}</p>
            <p className="text-sm/6 font-medium text-gray-400">合計試合数</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-2xl font-bold">
              {winning_rate} <span className="text-sm text-gray-400">%</span>
            </p>
            <p className="text-sm/6 font-medium text-gray-400">勝率</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-2xl font-bold">
              {scoring_rate} <span className="text-sm text-gray-400">点</span>
            </p>
            <p className="text-sm/6 font-medium text-gray-400">得点効率(得点/失点)</p>
          </div>
        </section>

        <section className="px-6 pb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">直近の試合結果</h2>
            <CLink asChild>
              <Link href={my_games_path()}>もっと見る</Link>
            </CLink>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white shadow rounded-lg divide-y divide-gray-200">
              <thead className="text-gray-600">
                <tr>
                  <th className="px-4 py-2 text-left">対戦相手</th>
                  <th className="px-4 py-2 text-left">結果</th>
                  <th className="px-4 py-2 text-left">日時</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {latest_games.map(game => (
                  <tr key={game.id}>
                    <td className="px-4 py-2">
                      <CLink asChild>
                        <Link href={player_path(game.player2.player_id)} className="block">
                          {game.player2.display_name}
                          <p className="text-xs text-gray-500">ID: {game.player2.player_id}</p>
                          <p className="text-xs text-gray-500">RT: {game.player2_rating_score}</p>
                        </Link>
                      </CLink>
                    </td>
                    <td className="px-4 py-2 text-nowrap">
                      {game.player1_point > game.player2_point ? <span className="text-red-700 pr-2">W</span> : <span className="text-blue-700 pr-2">L</span>}
                      <span>{game.player1_point}</span>-<span>{game.player2_point}</span>
                    </td>
                    <td className="px-4 py-2">
                      {formatDate(game.created_at)}
                      <br />
                      {formatTime(game.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="px-6 pb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">参加中のイベント</h2>
            <CLink as={Link} href={events_path({ filter: "participating" })}>
              もっと見る
            </CLink>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white shadow rounded-lg divide-y divide-gray-200">
              <thead className="text-gray-600">
                <tr>
                  <th className="px-4 py-2 text-left">日時</th>
                  <th className="px-4 py-2 text-left">イベント名</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {participated_events.map(event => (
                  <tr key={event.id} className="cursor-pointer" onClick={() => handleRowClick(event)}>
                    <td className="px-4 py-2">
                      {formatDate(event.start_at)}
                      <br />
                      {formatTime(event.start_at)}
                    </td>
                    <td className="px-4 py-2">
                      <CLink as={Link} href={event_path(event.id)}>
                        {event.name}
                      </CLink>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
